/** @jsxRuntime classic */
/** @jsx jsx */
/*
License

Copyright (c) 2022 Chukwubuikem Ume-Ugwa

Permission is hereby witheld, any person obtaining a copy
of this software and associated documentation files (the "Software"), cannot
use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software. These restrictions can be only lifted with
a written permission from Chukwubuikem Ume-Ugwa.
*/
import {
  Text,
  Button,
  useDisclosure,
  Container,
  Image,
  Circle,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  useToast,
  Progress,
} from '@chakra-ui/react';
import { Menu, MenuButton, MenuList, MenuItem } from '@chakra-ui/react';
import QueueUpdateModal from './update.queue.component';
import QRCodeModal from './qrcode.component';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { lordWallet } from '..';
import { firebaseFacade } from '../api/firebase';
import { isOrphaned, log, MISTERY_WALLET } from '../utils';
import { css, jsx } from '@emotion/react';
import ShareIcon from '@mui/icons-material/Share';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import Track from './track.component';
import ArrowBackIos from '@mui/icons-material/ArrowBackIos';
import { PublicKey } from '@solana/web3.js';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { SendTransaction } from '../api/qdot';
import PaymentIcon from '@mui/icons-material/Payment';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import Schemas from '../api/schemas';
import TrackUpload from './track.upload.component';

export function QueuePage(props) {
  const {
    openq,
    streamingqAddress,
    isPlaying,
    currentTrackAddress,
    onPopView,
    associatedTokenAddress,
    onStream,
  } = props;

  const [tracks, setTracks] = useState([]);
  const [innerQueue, setQueue] = useState(openq);
  const [openqAddress] = useState(openq.queue_address);
  const { name, number_of_tracks, number_of_hours, art_uri, queue_address } =
    innerQueue;

  const [innerIsPlaying, setInnerPlaying] = useState(false);
  const [sharedUrl, setSharedUrl] = useState();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [uploading, setUploading] = useState(false);

  const {
    isOpen: isQROpen,
    onOpen: onQROpen,
    onClose: onQRClose,
  } = useDisclosure();

  const {
    isOpen: isTrackUploadOpen,
    onOpen: onOpenTrackUpload,
    onClose: onCloseTrackUpload,
  } = useDisclosure();

  const { publicKey, signTransaction } = useWallet();
  const containerDivRef = useRef();
  const imageRef = useRef();

  const [imageOffset, setImageOffset] = useState(40);
  const [subscribed, setSubscribed] = useState(true);
  const [subscribable, setSubscribable] = useState(false);

  const toast = useToast();
  const { connection } = useConnection();

  const handleDelete = () => {
    onDelete(openqAddress);
  };

  const handleShare = () => {
    onQROpen();
    setSharedUrl(`https://musicq.club/stream.view/${queue_address}`);
  };

  const handleEdit = () => {
    onOpen();
  };

  const handlePlay = useCallback(() => {
    onStream(
      openqAddress,
      innerQueue,
      !innerIsPlaying,
      streamingqAddress === openqAddress,
      tracks
    );
    firebaseFacade.updateMonthlyStreams(innerQueue);
  }, [
    streamingqAddress,
    openqAddress,
    currentTrackAddress,
    innerIsPlaying,
    tracks,
  ]);

  const handleBack = useCallback(() => {
    log({ component: 'QueuePage', msg: 'back pressed!' });
    onPopView();
  });

  const handleSubscribe = async () => {
    const { subscription } = innerQueue;
    const { price, schedule } = subscription;
    const verdict = await SendTransaction(
      connection,
      new PublicKey(associatedTokenAddress),
      publicKey,
      new PublicKey(innerQueue.owner_wallet_address),
      price,
      signTransaction
    );

    if (verdict) {
      try {
        await firebaseFacade.addSubscriber(publicKey, innerQueue, schedule);
        setSubscribed(true);
        firebaseFacade.updateMonthlySubscribers(innerQueue);

        firebaseFacade.updateMonthlyGrossing(innerQueue, price);
        return;
      } catch (error) {}
    }
    toast({
      title: 'Subscrition Failed',
      description: `Failed to subcribe you. Please try again!`,
      duration: 5000,
      isClosable: true,
      status: 'info',
      position: 'top',
    });
  };

  const handleClaim = useCallback(async () => {
    try {
      await firebaseFacade.updateDocument(
        `${Schemas.db_node_schema.queues}/${innerQueue.queue_address}`,
        { owner_wallet_address: publicKey.toBase58() }
      );

      setQueue({ ...innerQueue, owner_wallet_address: publicKey.toBase58() });
      toast({
        title: 'Claimed',
        description: `You're now the owner of the queue. Make changes!`,
        duration: 5000,
        isClosable: true,
        status: 'success',
        position: 'bottom',
      });
    } catch (error) {
      toast({
        title: 'Claim Failed',
        description: `Failed to claim the queue for you. Please try again!`,
        duration: 5000,
        isClosable: true,
        status: 'error',
        position: 'bottom',
      });
    }
  }, [innerQueue]);

  const renderMenu = () => {
    log({
      component: 'QueuePage',
      msg: 'renderMenu called',
      publicKey: publicKey,
    });
    if (publicKey) {
      if (
        innerQueue.owner_wallet_address === publicKey.toBase58() ||
        publicKey.equals(lordWallet)
      ) {
        log({
          component: 'QueuePage',
          msg: 'rendered owner menu',
          publicKey: publicKey,
        });
        return (
          <Menu>
            <MenuButton color={'yellow.400'}>
              <MoreVertIcon />
            </MenuButton>
            <MenuList>
              <MenuItem onClick={handleEdit}>
                <EditIcon /> Edit
              </MenuItem>
              <MenuItem onClick={handleDelete}>
                <DeleteIcon /> Delete
              </MenuItem>
            </MenuList>
          </Menu>
        );
      } else {
        log({
          component: 'QueuePage',
          msg: 'rendered menu',
          publicKey: publicKey,
        });

        function notClaimed() {
          return innerQueue.owner_wallet_address === MISTERY_WALLET;
        }

        function canSubscribe() {
          return subscribable && !subscribed;
        }

        return (
          (notClaimed() || canSubscribe()) && (
            <Menu>
              <MenuButton color={'yellow.400'}>
                <MoreVertIcon />
              </MenuButton>
              <MenuList>
                {canSubscribe() && (
                  <MenuItem onClick={handleSubscribe}>
                    <PaymentIcon /> Subscribe
                  </MenuItem>
                )}
                {notClaimed() && (
                  <MenuItem onClick={handleClaim}>
                    <FavoriteBorderIcon /> Claim
                  </MenuItem>
                )}
              </MenuList>
            </Menu>
          )
        );
      }
    }
  };

  const updateListener = useCallback(updatedQueue => {
    setQueue(updatedQueue);
  }, []);

  const onDelete = () => {
    if (publicKey && publicKey.equals(lordWallet)) {
      firebaseFacade
        .deleteQueue(innerQueue.queue_address)
        .then(_ => onPopView())
        .catch(_ => {
          toast({
            title: 'Unexpected Error',
            description:
              'Error while trying to delete queue. Please try again!',
            duration: 5000,
            isClosable: true,
            status: 'error',
            position: 'top',
          });
        });
    } else if (
      !publicKey ||
      publicKey.toBase58() !== innerQueue.owner_wallet_address
    ) {
      toast({
        title: 'Unauthorized Action',
        description: 'You do not have authority over this queue!',
        duration: 5000,
        isClosable: true,
        status: 'error',
        position: 'top',
      });
    } else {
      firebaseFacade
        .deleteQueue(innerQueue.queue_address)
        .then(_ => onPopView())
        .catch(_ => {
          toast({
            title: 'Unexpected Error',
            description:
              'Error while trying to delete queue. Please try again!',
            duration: 5000,
            isClosable: true,
            status: 'error',
            position: 'top',
          });
        });
    }
  };

  const onTrackLoaded = useCallback(
    async tracks => {
      log({
        component: 'QueuePage',
        msg: 'onTrackLoaded called',
        tracks: tracks,
      });
      setTracks(tracks);
      if (streamingqAddress === openqAddress) {
        log({ component: 'QueuePage', msg: 'Forwarding new tracks to stream' });
        onStream(
          openqAddress,
          innerQueue,
          !innerIsPlaying,
          streamingqAddress === openqAddress,
          tracks
        );
      } else {
        log({
          component: 'QueuePage',
          msg: 'Not forwarding new tracks to stream',
          streamingqAddress: streamingqAddress,
          openqAddress: openqAddress,
        });
      }
      for (let idx = 0; idx < tracks.length; idx++) {
        await firebaseFacade.loadAudio(tracks[idx]);
      }
    },
    [streamingqAddress]
  );

  const onUploadStart = useCallback(() => {
    setUploading(true);
  }, []);

  const onFinished = useCallback(successfulUpload => {
    toast({
      title: `${successfulUpload} Track(s) Uploaded`,
      description: 'Your tracks were uploaded successfully',
      duration: 5000,
      isClosable: true,
      status: 'success',
      position: 'top',
    });
    setUploading(false);
  }, []);

  const collabEnable = useCallback(() => {
    return (
      !innerQueue.locked ||
      (publicKey !== null &&
        publicKey.toBase58() === innerQueue.owner_wallet_address)
    );
  }, [publicKey, innerQueue]);

  const handleTrackUpload = () => {
    if (!publicKey) {
      toast({
        title: 'No Wallet Found.',
        description:
          'You need to connect a wallet to earn QDOT! Any track created without wallet will be Orphaned',
        duration: 1000,
        isClosable: true,
        status: 'warning',
        variant: 'top-accent',
        position: 'bottom-right',
      });
    }
    onOpenTrackUpload();
  };

  useEffect(
    () => firebaseFacade.listenForTrackChanges(onTrackLoaded, innerQueue),
    [innerQueue, streamingqAddress]
  );

  useEffect(() => {
    const divWidth = containerDivRef.current.clientWidth;
    const imageWidth = imageRef.current.clientWidth;
    setImageOffset((divWidth / 2 - imageWidth / 2).toFixed());
  }, [imageOffset]);

  useEffect(() => {
    if (publicKey) {
      firebaseFacade.isSubscriber(publicKey, innerQueue).then(setSubscribed);
    } else {
      setSubscribed(false);
    }
    setSubscribable(
      innerQueue.subscription &&
        publicKey &&
        innerQueue.owner_wallet_address !== publicKey.toBase58()
    );
  }, [publicKey, innerQueue]);

  useEffect(() => {
    setInnerPlaying(isPlaying && openqAddress === streamingqAddress);
  }, [isPlaying, streamingqAddress]);

  useEffect(() => firebaseFacade.listenForQueueChanges(setQueue, innerQueue), []);

  log({ component: 'QueuePage', msg: 'rendered', tracks: tracks });
  return (
    <Container borderRadius={'2xl'}>
      <div ref={containerDivRef}>
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            padding: 16px;
          `}
        >
          <Button
            onClick={handleBack}
            color={'yellow.400'}
            backgroundColor={'transparent'}
          >
            <ArrowBackIos />
          </Button>
          {renderMenu()}
        </div>
        {isOrphaned(innerQueue) && (
          <Alert status="info" mb={'8px'}>
            <AlertIcon />
            <AlertTitle>Orphaned Queue!</AlertTitle>
            <AlertDescription>
              Queue will be deleted after 48hrs. To avoid this, connect your
              wallet and claim it.
            </AlertDescription>
          </Alert>
        )}
        <Image
          src={
            art_uri || `${process.env.PUBLIC_URL}/android-chrome-512x512.png`
          }
          borderRadius={'2xl'}
          shadow={'2xl'}
          height={['190px', '150px', '150px', '250px', '250px']}
          width={['190px', '150px', '150px', '250px', '250px']}
          position={'relative'}
          left={`${imageOffset}px`}
          ref={imageRef}
        />
        <Text
          align={'start'}
          textAlign={'start'}
          color={'white'}
          fontSize={'2xl'}
          fontWeight={'bold'}
        >
          {name}
        </Text>
        <Text
          align={'start'}
          textAlign={'start'}
          color={'white'}
          fontWeight={'thin'}
        >{`${number_of_tracks} tracks, ${number_of_hours} hrs`}</Text>
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            padding: 10px;
          `}
        >
          <Circle
            color={'white'}
            onClick={handleShare}
            sx={{ cursor: 'pointer' }}
          >
            <ShareIcon />
          </Circle>
          <Circle
            bgColor={'yellow.400'}
            color={'white'}
            onClick={handlePlay}
            sx={{ cursor: 'pointer' }}
            size={'50'}
          >
            {innerIsPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </Circle>
        </div>
        {tracks.map(track => (
          <Track
            {...props}
            isPlaying={innerIsPlaying}
            key={track.track_address}
            track={track}
            tracks={tracks}
          />
        ))}
        {uploading && (
          <Progress colorScheme={'yellow'} size="xs" isIndeterminate />
        )}
        <Box height={'60px'}></Box>
        <QueueUpdateModal
          isOpen={isOpen}
          onClose={onClose}
          queue={innerQueue}
          onUpdateListener={updateListener}
        />
        {collabEnable() && (
          <Button
            onClick={handleTrackUpload}
            position="fixed"
            shadow={'dark-lg'}
            borderRadius="50%"
            height={['40px', '60px']}
            width={['40px', '60px']}
            bottom="100px"
            right={[8, 16, 32, 64, 128, 256]}
            bg={'white'}
            color={'black'}
            zIndex={1}
          >
            <span class="material-icons">add</span>
          </Button>
        )}
        <QRCodeModal isOpen={isQROpen} onClose={onQRClose} url={sharedUrl} />
        <TrackUpload
          isOpen={isTrackUploadOpen}
          onClose={onCloseTrackUpload}
          onUploadStart={onUploadStart}
          onFinished={onFinished}
          queue={openq}
        />
      </div>
    </Container>
  );
}
